// src/pages/Utilisateurs/components/UserDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes, FaUserPlus, FaUserEdit } from "react-icons/fa";

import { getUserId } from "../usersAdminUtils";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

export default function UserDrawer({
  mode,
  user,
  roleOptions,
  isOpen,
  onClose,
  onSubmit,
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    nom: "",
    prenom: "",
    email: "",
    role_id: "",
  });

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      nom: user?.nom || "",
      prenom: user?.prenom || "",
      email: user?.email || "",
      role_id: user?.role_id || "",
    });
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, onClose]);

  const title = useMemo(() => {
    return isEdit ? "Éditer un utilisateur" : "Ajouter un utilisateur";
  }, [isEdit]);

  if (!isOpen) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: name === "role_id" ? Number(value) : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    onSubmit({
      nom: form.nom.trim(),
      prenom: form.prenom.trim(),
      email: form.email.trim(),
      role_id: Number(form.role_id),
    });
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950">
        <div className="relative overflow-hidden bg-slate-950 p-5 text-white sm:p-6">
          <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-indigo-500/30 blur-3xl" />
          <div className="absolute -bottom-16 left-1/3 h-36 w-36 rounded-full bg-pink-500/20 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                {isEdit ? <FaUserEdit /> : <FaUserPlus />}
              </div>

              <h2 className="text-2xl font-bold">{title}</h2>

              <p className="mt-1 text-sm text-slate-300">
                {isEdit
                  ? `Modification du compte #${getUserId(user)}.`
                  : "Création d’un nouveau compte utilisateur."}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-4">
            <Field label="Nom">
              <input
                className={INPUT}
                type="text"
                name="nom"
                value={form.nom}
                onChange={handleChange}
                required
              />
            </Field>

            <Field label="Prénom">
              <input
                className={INPUT}
                type="text"
                name="prenom"
                value={form.prenom}
                onChange={handleChange}
                required
              />
            </Field>

            <Field label="Email">
              <input
                className={INPUT}
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </Field>

            <Field label="Rôle / Formule">
              <select
                className={INPUT}
                name="role_id"
                value={form.role_id}
                onChange={handleChange}
                required
              >
                <option value="">Sélectionner un rôle</option>

                {roleOptions.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </form>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
            >
              <FaSave />
              {isEdit ? "Mettre à jour" : "Créer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </span>
      {children}
    </label>
  );
}